import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send, Clock, CheckCheck } from 'lucide-react';
import { useSite } from '../context/SiteContext';

// Lightweight WhatsApp icon (green square + phone glyph)
const WhatsAppIcon = ({ className = '' }) => (
  <svg viewBox="0 0 32 32" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.003 3C9.375 3 4 8.373 4 15c0 2.116.552 4.185 1.6 6.005L4 29l8.164-1.567A11.94 11.94 0 0 0 16.003 27C22.63 27 28 21.628 28 15S22.63 3 16.003 3zm6.928 17.09c-.293.823-1.699 1.575-2.372 1.676-.606.09-1.373.128-2.216-.14-.51-.16-1.163-.376-2.001-.734-3.525-1.52-5.826-5.06-6.001-5.29-.176-.229-1.44-1.917-1.44-3.66 0-1.743.918-2.6 1.243-2.955.322-.354.706-.443.94-.443l.678.012c.216.01.507-.082.792.605.293.706.996 2.44 1.083 2.616.088.176.147.383.03.612-.117.229-.176.373-.352.573-.176.2-.371.446-.53.599-.176.176-.36.367-.155.72.205.353.912 1.505 1.958 2.437 1.345 1.198 2.478 1.57 2.831 1.746.354.176.559.147.766-.088.207-.235.882-1.03 1.117-1.383.235-.353.47-.294.792-.176.323.117 2.044.965 2.396 1.14.353.177.588.264.676.412.088.147.088.852-.205 1.674z" />
  </svg>
);

const formatTime = () => {
  try {
    return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (_) {
    return '';
  }
};

const WhatsAppWidget = () => {
  const { whatsapp } = useSite();
  const [open, setOpen] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const [msg, setMsg] = useState('');
  const [nowLabel] = useState(formatTime());
  const timerRef = useRef(null);

  // Teaser bubble timer (once per session)
  useEffect(() => {
    if (!whatsapp?.enabled) return undefined;
    if (typeof window === 'undefined') return undefined;
    const dismissed = window.sessionStorage.getItem('rec_wa_teaser_dismissed');
    const delay = Number(whatsapp?.teaserDelay ?? 0);
    if (dismissed || delay <= 0) return undefined;
    timerRef.current = setTimeout(() => setShowTeaser(true), delay * 1000);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [whatsapp?.enabled, whatsapp?.teaserDelay]);

  if (!whatsapp?.enabled || !whatsapp?.phone) return null;

  const dismissTeaser = () => {
    setShowTeaser(false);
    try { window.sessionStorage.setItem('rec_wa_teaser_dismissed', '1'); } catch (_) { /* ignore */ }
  };

  const openWhatsApp = (customMessage) => {
    const text = (customMessage ?? msg ?? '').trim();
    const encoded = encodeURIComponent(text || 'Hi Recreasol, I would like to know more about your solutions.');
    const url = `https://wa.me/${whatsapp.phone}?text=${encoded}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setMsg('');
    setOpen(false);
    dismissTeaser();
  };

  const toggle = () => {
    setOpen((v) => !v);
    dismissTeaser();
  };

  const avatar = whatsapp.agentAvatar;

  return (
    <>
      {/* Floating widget wrapper */}
      <div className="fixed z-[60] right-4 md:right-6 bottom-4 md:bottom-6 flex flex-col items-end gap-3 pointer-events-none">
        {/* Teaser bubble */}
        {showTeaser && !open && (
          <div className="pointer-events-auto max-w-[240px] rounded-2xl rounded-br-md bg-white text-slate-800 shadow-2xl px-3.5 py-2.5 text-sm flex items-start gap-2 fade-up">
            <span className="flex-1">{whatsapp.teaser}</span>
            <button
              onClick={dismissTeaser}
              className="text-slate-400 hover:text-slate-700 btn-transition"
              aria-label="Dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Chat panel */}
        {open && (
          <div className="pointer-events-auto w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl overflow-hidden shadow-2xl border border-black/10 bg-white text-slate-800 flex flex-col fade-up">
            {/* Header */}
            <div className="px-4 py-3.5 text-white relative" style={{ background: '#075E54' }}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="h-10 w-10 rounded-full overflow-hidden bg-white/10 border border-white/20 flex items-center justify-center">
                    {avatar ? (
                      <img src={avatar} alt={whatsapp.agentName} className="w-full h-full object-cover" />
                    ) : (
                      <WhatsAppIcon className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#25D366] border-2 border-[#075E54]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{whatsapp.agentName}</div>
                  <div className="text-[11px] text-white/80 truncate">{whatsapp.agentRole} · Online</div>
                </div>
                <button
                  onClick={toggle}
                  className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center btn-transition"
                  aria-label="Close chat"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Chat body */}
            <div
              className="px-4 py-4 flex-1 max-h-[320px] overflow-y-auto"
              style={{
                backgroundColor: '#ECE5DD',
                backgroundImage:
                  'radial-gradient(rgba(0,0,0,0.03) 1px, transparent 1px)',
                backgroundSize: '18px 18px',
              }}
            >
              {/* Greeting bubble */}
              <div className="flex gap-2 items-end mb-3">
                <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white text-slate-800 px-3.5 py-2.5 text-sm shadow-sm">
                  {whatsapp.greeting}
                  <div className="mt-1 text-[10px] text-slate-400 text-right flex items-center justify-end gap-0.5">
                    {nowLabel}
                  </div>
                </div>
              </div>

              {/* Business hours bubble */}
              {whatsapp.hours && (
                <div className="flex gap-2 items-end mb-3">
                  <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white text-slate-800 px-3.5 py-2.5 text-sm shadow-sm">
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1">
                      <Clock className="h-3 w-3" /> Business hours
                    </div>
                    {whatsapp.hours}
                    <div className="mt-1 text-[10px] text-slate-400 text-right">{nowLabel}</div>
                  </div>
                </div>
              )}

              {/* User draft (if typed) */}
              {msg.trim() && (
                <div className="flex justify-end gap-2 items-end mb-3">
                  <div className="max-w-[85%] rounded-2xl rounded-br-md px-3.5 py-2.5 text-sm shadow-sm" style={{ background: '#DCF8C6' }}>
                    <div className="whitespace-pre-wrap break-words">{msg}</div>
                    <div className="mt-1 text-[10px] text-slate-500 text-right flex items-center justify-end gap-0.5">
                      {nowLabel} <CheckCheck className="h-3 w-3" />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-slate-200 bg-white">
              <div className="flex items-end gap-2">
                <textarea
                  value={msg}
                  onChange={(e) => setMsg(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      openWhatsApp();
                    }
                  }}
                  rows={1}
                  placeholder={whatsapp.inputPlaceholder || 'Type your message…'}
                  className="flex-1 resize-none rounded-2xl bg-slate-100 border border-slate-200 px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#25D366]/40 focus:bg-white"
                  style={{ maxHeight: 100 }}
                />
                <button
                  onClick={() => openWhatsApp()}
                  aria-label="Send via WhatsApp"
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white shadow-md btn-transition hover:scale-105"
                  style={{ background: '#25D366' }}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => openWhatsApp()}
                className="mt-2.5 w-full text-white text-sm font-medium rounded-xl py-2.5 flex items-center justify-center gap-2 btn-transition hover:opacity-90"
                style={{ background: '#25D366' }}
              >
                <WhatsAppIcon className="h-4 w-4" /> {whatsapp.ctaLabel || 'Start Chat on WhatsApp'}
              </button>
              <div className="mt-2 text-center text-[10px] text-slate-400">
                Powered by WhatsApp · {whatsapp.displayPhone}
              </div>
            </div>
          </div>
        )}

        {/* Floating action button */}
        <button
          onClick={toggle}
          aria-label={open ? 'Close chat' : 'Chat on WhatsApp'}
          className="pointer-events-auto h-14 w-14 rounded-full flex items-center justify-center text-white shadow-xl btn-transition hover:scale-105"
          style={{
            background: '#25D366',
            boxShadow: '0 10px 30px -8px rgba(37,211,102,0.6), 0 4px 12px rgba(0,0,0,0.2)',
          }}
        >
          {open ? <X className="h-6 w-6" /> : <WhatsAppIcon className="h-7 w-7" />}
          {!open && whatsapp.teaserDelay > 0 && (
            <span className="absolute inset-0 rounded-full animate-ping opacity-40" style={{ background: '#25D366' }} />
          )}
        </button>
      </div>
    </>
  );
};

// Small inline "Chat on WhatsApp" button - reusable inside Contact section, etc.
export const WhatsAppInlineButton = ({ prefill, className = '' }) => {
  const { whatsapp } = useSite();
  if (!whatsapp?.enabled || !whatsapp?.phone) return null;
  const text = encodeURIComponent(prefill || 'Hi Recreasol, I would like to know more about your solutions.');
  const href = `https://wa.me/${whatsapp.phone}?text=${text}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 h-11 px-4 rounded-md text-white font-medium btn-transition hover:opacity-90 ${className}`}
      style={{ background: '#25D366' }}
    >
      <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
    </a>
  );
};

export default WhatsAppWidget;
